import * as React from "react"
import { RouteComponentProps, WindowLocation } from "@gatsbyjs/reach-router"
import { navigate } from "gatsby"
import * as qs from "query-string"
import {
  Link,
  Heading,
  Spacer,
  Card,
  CardHeader,
  CardSection,
  Button,
  ThemeCss,
} from "gatsby-interface"
import { StandardSingleColumn } from "@modules/ui/layouts/Containers"
import BasicConfiguration from "@modules/site/create/Import/components/BasicConfiguration"
import CmsIntegrationCard from "@modules/site/create/Import/components/CmsIntegrationCard"
import { Stepper, Step } from "@modules/site/create/shared/components/Stepper"
import { EnvironmentVariablesCard } from "@modules/site/create/Import/components/EnvironmentVariablesCard"
import { useOrganizationNameFieldsFragment } from "@modules/organization/Settings/fragments.generated"
import { useOrganizationBaseDetailsQuery } from "@modules/organization/queries.generated"
import { SourceControlProvider } from "@modules/graphql/types"
import { usePublishSite } from "@modules/site/create/shared/hooks/usePublishSite"
import { getPathToSiteDetails } from "@modules/site/details/utils"
import { getPathToOrgDetails } from "@modules/organization/shared/utils"
import { MdArrowBack } from "react-icons/md"
import { getPathToCreateSite } from "@modules/site/create/shared/utils"
import useCreateSiteState, {
  CreateSiteType,
  SiteConfigurationStep,
} from "@modules/site/create/shared/hooks/useCreateSiteState"
import { headingCss } from "../../shared/components/SiteCreationWizardHeader"
import { createSiteConfiguration as text } from "@modules/locales/default"
import { FormattedMessage, interpolateMessage } from "@modules/locales"

export type SiteConfigurationProps = RouteComponentProps<{
  organizationId: string
  onCreate?: (org: string, site: string) => void
}>

export function SiteConfiguration({
  organizationId = "",
  location = {} as WindowLocation,
  onCreate,
}: SiteConfigurationProps) {
  const { step, setStep, siteId, setSiteId } = useCreateSiteState(
    CreateSiteType.Import
  )

  const [publishSite, { loading: isPublishing, errorAlert: publishError }] =
    usePublishSite(siteId || "")

  const searchParams = qs.parse(location?.search || ``)
  const installationId = searchParams.installation as string
  const vcsConfigId = searchParams.configId as string
  const gitProvider = searchParams.provider as SourceControlProvider
  const repoName = searchParams.repository as string

  if (!organizationId) {
    navigate(`/dashboard`)
  }

  if (!installationId || !repoName || !gitProvider) {
    navigate(getPathToOrgDetails(organizationId))
  }

  return (
    <StandardSingleColumn
      css={theme => ({
        display: `grid`,
        gridTemplateColumns: `3fr 1fr`,
        gridTemplateAreas: `"header header"
                            "main main"`,
        gap: theme.space[10],
        marginTop: theme.space[8],
        paddingBottom: "3rem",

        [theme.mediaQueries.tablet]: {
          gridTemplateAreas: `"header header"
                              "main aside"`,
          paddingBottom: "7.5rem",
        },
      })}
    >
      <SiteConfigurationHeader
        css={{ gridArea: `header` }}
        organizationId={organizationId}
        location={location}
        repositoryName={repoName}
      />
      <main css={{ gridArea: `main` }}>
        <Card>
          <CardHeader
            css={theme => ({
              h3: {
                fontFamily: theme.fonts.heading,
              },
            })}
            title={text.headers.basicConfiguration}
          />
          <CardSection>
            <BasicConfiguration
              installationId={installationId}
              organizationId={organizationId}
              repository={repoName}
              provider={gitProvider}
              vcsConfigId={vcsConfigId}
              isCurrentStep={step === SiteConfigurationStep.BasicConfiguration}
              onSiteCreated={(newSiteId: string) => {
                setSiteId(newSiteId)
                setStep(SiteConfigurationStep.EnvironmentVariables)
              }}
            />
          </CardSection>
        </Card>
        <Spacer size={8} />
        <CmsIntegrationCard
          siteId={siteId}
          onNext={() => {
            setStep(SiteConfigurationStep.EnvironmentVariables)
          }}
        />
        <Spacer size={8} />
        <EnvironmentVariablesCard
          uiSource="New Site Wizard: setup"
          organizationId={organizationId}
          siteId={siteId || null}
          defaultEditMode={true}
        />
        <Spacer size={8} />
        {publishError && <React.Fragment>{publishError}</React.Fragment>}
        <Button
          data-ld="create-site"
          onClick={() =>
            publishSite(() => {
              if (typeof onCreate === "function") {
                onCreate(organizationId, siteId)
              } else {
                navigate(
                  `${getPathToSiteDetails(siteId, organizationId)}?newsite=true`
                )
              }
            })
          }
          loading={isPublishing}
          disabled={
            !siteId || step !== SiteConfigurationStep.EnvironmentVariables
          }
        >
          {text.actions.buildSite}
        </Button>
      </main>
      <SiteConfigurationAside
        css={theme => ({
          gridArea: `aside`,
          display: `none`,
          [theme.mediaQueries.tablet]: { display: `block` },
        })}
        currentStep={step}
      />
    </StandardSingleColumn>
  )
}

/*
Header
*/

const highlightCss: ThemeCss = theme => ({
  color: theme.colors.gatsby,
})
function SiteConfigurationHeader({
  organizationId,
  location,
  repositoryName,
  className,
}: {
  organizationId: string
  location: WindowLocation
  repositoryName?: string
  className?: string
}): JSX.Element {
  const { data: organizationNameFieldsData } =
    useOrganizationNameFieldsFragment(organizationId)

  const { data: organizationBaseDetailsData } = useOrganizationBaseDetailsQuery(
    {
      variables: { id: organizationId },
      skip: !!organizationNameFieldsData,
    }
  )

  const goBackTo =
    (qs.parse(location.search).returnTo as string) ||
    getPathToCreateSite(organizationId)

  const goBackLink = {
    to: goBackTo,
    label: text.actions.goBack,
  }

  const workspaceName = organizationNameFieldsData
    ? organizationNameFieldsData.name
    : organizationBaseDetailsData?.organizationDetails?.name

  return (
    <header className={className}>
      <Link
        variant="SIMPLE"
        to={goBackLink.to}
        css={theme => ({
          display: `flex`,
          alignItems: `center`,
          textDecoration: `none`,
          textTransform: `capitalize`,
          svg: {
            marginRight: theme.space[3],
          },
        })}
      >
        <MdArrowBack /> {goBackLink.label}
      </Link>
      <Spacer size={3} />
      <Heading
        as="h2"
        css={(theme: Theme) => [headingCss(theme), { marginTop: 0 }]}
      >
        {interpolateMessage<"repositoryName">(text.messages.addRepository, {
          repositoryName: repositoryName ? repositoryName : `a new site`,
        })}{" "}
        <FormattedMessage<"workspaceName", "span">
          message={text.messages.toWorkspace}
          values={{
            workspaceName: workspaceName || "",
          }}
          tags={{
            span: function (content) {
              if (!content) return null
              return <span css={highlightCss}>{content}</span>
            },
          }}
        />
      </Heading>
    </header>
  )
}

function SiteConfigurationAside({
  currentStep,
  className,
}: {
  currentStep: number
  className?: string
}) {
  return (
    <aside className={className}>
      <Stepper currentStep={currentStep}>
        {text.labels.steps.map(text => (
          <Step key={text} text={text} />
        ))}
      </Stepper>
    </aside>
  )
}
